import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { Html5QrcodeScanner, Html5Qrcode } from 'html5-qrcode';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5050';

export default function QCCheckScanner() {
  const [barcode, setBarcode] = useState('');
  const [lotDetails, setLotDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isScanning, setIsScanning] = useState(false);

  // Failure Form State
  const [showFailForm, setShowFailForm] = useState(false);
  const [defectCategory, setDefectCategory] = useState('');
  const [actualQty, setActualQty] = useState('');
  const [comments, setComments] = useState('');
  const [photos, setPhotos] = useState([]);

  const fileInputRef = useRef(null);
  const barcodeImageRef = useRef(null);

  useEffect(() => {
    if (isScanning) {
      const scanner = new Html5QrcodeScanner("reader", { 
        qrbox: { width: 250, height: 100 }, 
        fps: 10 
      }, false);
      
      scanner.render((decodedText) => {
        scanner.clear();
        setIsScanning(false);
        setBarcode(decodedText);
        fetchLotDetails(decodedText);
      }, (error) => {
        // ignore errors while scanning
      });

      return () => {
        try { scanner.clear(); } catch(e) {}
      };
    }
  }, [isScanning]);

  const handleBarcodeImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const html5QrCode = new Html5Qrcode("hidden-reader");
    html5QrCode.scanFile(file, true)
      .then(decodedText => {
        setBarcode(decodedText);
        fetchLotDetails(decodedText);
      })
      .catch(err => {
        Swal.fire('Scan Failed', 'Could not detect any barcode in the uploaded image. Please try another clear image.', 'error');
      })
      .finally(() => {
        e.target.value = '';
        try { html5QrCode.clear(); } catch(e) {}
      });
  };

  const fetchLotDetails = async (code) => {
    if (!code.trim()) return;
    setLoading(true);
    setLotDetails(null);
    setShowFailForm(false);
    
    try {
      const res = await axios.get(`${BASE_URL}/api/wip-dashboard/qc/lot/${code.trim()}`, { withCredentials: true });
      if (res.data.success) {
        setLotDetails(res.data.data);
        if (res.data.data.open_ticket_id) {
          Swal.fire('Active Defect Open', `An active defect ticket (#${res.data.data.open_ticket_id}) is already open for this lot! It is waiting for Manager action.`, 'warning');
        }
      }
    } catch (err) {
      Swal.fire('Error', err.response?.data?.message || 'Failed to fetch lot details', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleScan = async (e) => {
    e.preventDefault();
    fetchLotDetails(barcode);
  };

  const submitQC = async (status) => {
    if (status === 'FAIL') {
      if (!defectCategory) return Swal.fire('Validation Error', 'Please select a defect category.', 'warning');
      if (!actualQty) return Swal.fire('Validation Error', 'Please enter actual checked quantity.', 'warning');
      if (photos.length === 0) return Swal.fire('Validation Error', 'Please upload at least one evidence photo for defects.', 'warning');
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('lot_id', lotDetails.lot_id);
      formData.append('process_id', lotDetails.process_id);
      formData.append('status', status);
      formData.append('checked_by', 'QC_Inspector'); 

      if (status === 'FAIL') {
        formData.append('defect_category', defectCategory);
        formData.append('expected_qty', lotDetails.expected_qty || 0);
        formData.append('actual_qty', actualQty);
        formData.append('comments', comments);
        photos.forEach(file => {
          formData.append('evidence_photos', file);
        });
      }

      const res = await axios.post(`${BASE_URL}/api/wip-dashboard/qc/submit`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      });

      if (res.data.success) {
        Swal.fire('Success', res.data.message, 'success');
        resetScanner();
      }
    } catch (err) {
      Swal.fire('Error', err.response?.data?.message || 'Submission failed.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const resetScanner = () => {
    setBarcode('');
    setLotDetails(null);
    setShowFailForm(false);
    setDefectCategory('');
    setActualQty('');
    setComments('');
    setPhotos([]);
  };

  return (
    <div style={{ backgroundColor: "#e2e8f0", minHeight: "100vh", paddingBottom: "40px", fontFamily: "'Inter', sans-serif" }}>
      
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
          #reader { width: 100%; border: none !important; border-radius: 12px; overflow: hidden; }
          #reader video { object-fit: cover; }
          #reader__dashboard_section_csr button { background: #4f46e5; color: white; border: none; padding: 8px 16px; border-radius: 8px; font-weight: 600; cursor: pointer; }
          #reader__dashboard_section_swaplink { text-decoration: none; color: #4f46e5; font-weight: 600; }
          #reader div[style*="color: red"] { display: none !important; }
          #reader span[style*="color: red"] { display: none !important; }
          #reader .html5-qrcode-element { margin: 10px 0; }
        `}
      </style>

      {/* HEADER */}
      <div style={{ background: "linear-gradient(135deg, #1e1b4b 0%, #4338ca 100%)", padding: "30px 20px", color: "white", boxShadow: "0 10px 25px -5px rgba(67, 56, 202, 0.4)", borderBottomLeftRadius: "24px", borderBottomRightRadius: "24px" }}>
        <h3 style={{ margin: 0, fontWeight: 800, fontSize: "24px", display: "flex", alignItems: "center", gap: 12, justifyContent: "center", letterSpacing: "-0.5px" }}>
          <i className="bi bi-upc-scan" style={{ fontSize: "28px", color: "#a5b4fc" }}></i>
          QC Scanner
        </h3>
        <p style={{ margin: "8px 0 0 0", color: "#e0e7ff", fontSize: "15px", textAlign: "center", fontWeight: 500 }}>Perform Shop-Floor Quality Checks</p>
      </div>

      <div style={{ padding: "30px 20px", display: "flex", justifyContent: "center", alignItems: "flex-start" }}>
        <div style={{ width: "100%", maxWidth: "550px" }}>
          
          {/* SCANNER INPUT BOX */}
          <div style={{ background: "rgba(255, 255, 255, 0.95)", backdropFilter: "blur(10px)", padding: "28px", borderRadius: "20px", boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)", border: "1px solid rgba(255,255,255,0.2)" }}>
            
            {isScanning ? (
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
                  <h6 style={{ margin: 0, fontWeight: 700, color: "#1e293b" }}>Camera Scanner Active</h6>
                  <button onClick={() => setIsScanning(false)} style={{ background: "#fef2f2", color: "#ef4444", border: "none", padding: "6px 12px", borderRadius: "8px", fontWeight: 700, fontSize: "13px" }}>Close</button>
                </div>
                <div id="reader"></div>
              </div>
            ) : (
              <form onSubmit={handleScan}>
                <label style={{ fontWeight: 700, color: "#334155", marginBottom: "12px", display: "block", fontSize: "15px" }}>Scan or Enter Lot Barcode</label>
                <div style={{ display: "flex", gap: "12px", flexDirection: "column" }}>
                  <div style={{ display: "flex", gap: "10px" }}>
                    <input 
                      type="text" 
                      value={barcode} 
                      onChange={(e) => setBarcode(e.target.value.toUpperCase())}
                      placeholder="e.g. RY235JULY..."
                      autoFocus
                      style={{ flex: 1, padding: "14px 18px", borderRadius: "12px", border: "2px solid #e2e8f0", fontSize: "16px", outline: "none", transition: "border-color 0.2s", fontWeight: 600, color: "#1e293b", backgroundColor: "#f8fafc" }}
                      onFocus={e => { e.target.style.borderColor = "#6366f1"; e.target.style.backgroundColor = "#fff"; }}
                      onBlur={e => { e.target.style.borderColor = "#e2e8f0"; e.target.style.backgroundColor = "#f8fafc"; }}
                      disabled={loading || submitting}
                    />
                    <button type="submit" disabled={loading || submitting || !barcode.trim()} 
                      style={{ background: "#4f46e5", color: "white", border: "none", padding: "0 24px", borderRadius: "12px", fontWeight: 700, transition: "background 0.2s", fontSize: "16px", boxShadow: "0 4px 6px -1px rgba(79, 70, 229, 0.4)" }}
                    >
                      {loading ? <i className="bi bi-arrow-repeat spin"></i> : 'Find'}
                    </button>
                  </div>
                  
                  <div style={{ display: "flex", alignItems: "center", margin: "10px 0" }}>
                    <div style={{ flex: 1, height: "1px", background: "#e2e8f0" }}></div>
                    <span style={{ padding: "0 10px", color: "#94a3b8", fontWeight: 600, fontSize: "13px" }}>OR</span>
                    <div style={{ flex: 1, height: "1px", background: "#e2e8f0" }}></div>
                  </div>

                  <div style={{ display: "flex", gap: "12px", flexDirection: "column" }}>
                    <button 
                      type="button" 
                      onClick={() => setIsScanning(true)}
                      style={{ background: "#0ea5e9", color: "white", border: "none", padding: "16px", borderRadius: "12px", fontWeight: 700, transition: "background 0.2s", fontSize: "16px", display: "flex", alignItems: "center", justifyContent: "center", gap: "10px", boxShadow: "0 4px 6px -1px rgba(14, 165, 233, 0.4)" }}
                    >
                      <i className="bi bi-camera" style={{ fontSize: "20px" }}></i>
                      Scan using Camera
                    </button>
                    
                    <button 
                      type="button" 
                      onClick={() => barcodeImageRef.current.click()}
                      style={{ background: "#fff", color: "#4f46e5", border: "2px solid #4f46e5", padding: "14px", borderRadius: "12px", fontWeight: 700, transition: "all 0.2s", fontSize: "16px", display: "flex", alignItems: "center", justifyContent: "center", gap: "10px" }}
                    >
                      <i className="bi bi-image" style={{ fontSize: "20px" }}></i>
                      Upload Image to Scan
                    </button>
                    <input 
                      type="file" 
                      ref={barcodeImageRef} 
                      onChange={handleBarcodeImageUpload} 
                      accept="image/*" 
                      style={{ display: "none" }} 
                    />
                  </div>
                </div>
              </form>
            )}
            <div id="hidden-reader" style={{ display: "none" }}></div>
          </div>

          {/* LOT DETAILS & ACTIONS */}
          {lotDetails && !showFailForm && (
            <div style={{ marginTop: "30px", background: "white", borderRadius: "20px", overflow: "hidden", boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1)", animation: "fadeIn 0.3s ease-out" }}>
              <div style={{ padding: "24px", borderBottom: "1px solid #f1f5f9", background: "#f8fafc" }}>
                <h5 style={{ margin: 0, color: "#1e293b", fontWeight: 800, letterSpacing: "-0.5px" }}>Lot Details</h5>
              </div>
              <div style={{ padding: "24px", display: "flex", flexDirection: "column", gap: "16px" }}>
                
                {lotDetails.open_ticket_id && (
                  <div style={{ background: "#fef2f2", color: "#991b1b", padding: "12px", borderRadius: "10px", fontWeight: 600, border: "1px solid #fecaca", display: "flex", alignItems: "center", gap: "10px" }}>
                    <i className="bi bi-exclamation-octagon-fill text-danger fs-5"></i>
                    <div>
                      An active Defect Ticket (#{lotDetails.open_ticket_id}) is already open. You cannot submit another report until the manager resolves it.
                    </div>
                  </div>
                )}

                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px", background: "#f1f5f9", borderRadius: "12px" }}>
                  <span style={{ color: "#475569", fontSize: "14px", fontWeight: 600 }}>Lot ID</span>
                  <span style={{ color: "#0f172a", fontWeight: 800, fontSize: "16px" }}>{lotDetails.lot_id}</span>
                </div>

                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px", background: "#f1f5f9", borderRadius: "12px" }}>
                  <span style={{ color: "#475569", fontSize: "14px", fontWeight: 600 }}>Current Department</span>
                  <span style={{ color: "#0ea5e9", fontWeight: 800, fontSize: "15px" }}>{lotDetails.process_name || 'N/A'}</span>
                </div>

                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px", background: "#f1f5f9", borderRadius: "12px" }}>
                  <span style={{ color: "#475569", fontSize: "14px", fontWeight: 600 }}>Expected Quantity</span>
                  <span style={{ color: "#0f172a", fontWeight: 800, fontSize: "16px" }}>{lotDetails.expected_qty || '-'}</span>
                </div>
                
                {(lotDetails.reported_rejected_qty > 0) && (
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px", background: "#fef2f2", borderRadius: "12px", border: "1px dashed #fca5a5" }}>
                    <span style={{ color: "#b91c1c", fontSize: "14px", fontWeight: 700 }}><i className="bi bi-exclamation-circle me-1"></i> Reported Rejected</span>
                    <span style={{ color: "#ef4444", fontWeight: 800, fontSize: "16px" }}>{lotDetails.reported_rejected_qty} pcs</span>
                  </div>
                )}

                {!lotDetails.open_ticket_id && (
                  <div style={{ display: "flex", gap: "16px", marginTop: "12px" }}>
                    <button 
                      onClick={() => setShowFailForm(true)}
                      style={{ flex: 1, padding: "16px", background: "#fff", border: "2px solid #ef4444", color: "#ef4444", borderRadius: "12px", fontWeight: 800, fontSize: "16px", transition: "all 0.2s" }}
                    >
                      <i className="bi bi-x-circle me-2"></i> QC Fail
                    </button>
                    <button 
                      onClick={() => submitQC('PASS')}
                      disabled={submitting}
                      style={{ flex: 1, padding: "16px", background: "#10b981", border: "none", color: "white", borderRadius: "12px", fontWeight: 800, fontSize: "16px", transition: "all 0.2s", boxShadow: "0 10px 15px -3px rgba(16, 185, 129, 0.4)" }}
                    >
                      <i className="bi bi-check-circle me-2"></i> Pass QC
                    </button>
                  </div>
                )}

              </div>
            </div>
          )}

          {/* FAIL FORM */}
          {showFailForm && (
            <div style={{ marginTop: "30px", background: "white", borderRadius: "20px", overflow: "hidden", boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1)", animation: "fadeIn 0.3s ease-out" }}>
              <div style={{ padding: "24px", background: "#fef2f2", borderBottom: "1px solid #fca5a5" }}>
                <h5 style={{ margin: 0, color: "#b91c1c", fontWeight: 800, display: "flex", alignItems: "center", gap: "10px", letterSpacing: "-0.5px" }}>
                  <i className="bi bi-exclamation-octagon-fill"></i> Report Defect
                </h5>
              </div>
              <div style={{ padding: "28px", display: "flex", flexDirection: "column", gap: "24px" }}>
                
                <div>
                  <label style={{ fontWeight: 700, color: "#334155", marginBottom: "10px", display: "block", fontSize: "15px" }}>Defect Category <span className="text-danger">*</span></label>
                  <select 
                    value={defectCategory}
                    onChange={e => setDefectCategory(e.target.value)}
                    style={{ width: "100%", padding: "14px", borderRadius: "12px", border: "1px solid #cbd5e1", outline: "none", fontWeight: 600, color: "#1e293b", backgroundColor: "#f8fafc" }}
                  >
                    <option value="">Select Category</option>
                    <option value="Torn/Hole">Torn / Hole</option>
                    <option value="Stains/Spots">Stains / Spots</option>
                    <option value="Stitching Issue">Stitching Issue</option>
                    <option value="Size Variance">Size Variance</option>
                    <option value="Wrong Material">Wrong Material</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div>
                  <label style={{ fontWeight: 700, color: "#334155", marginBottom: "10px", display: "block", fontSize: "15px" }}>Actual Checked Quantity <span className="text-danger">*</span></label>
                  <input 
                    type="number"
                    value={actualQty}
                    onChange={e => setActualQty(e.target.value)}
                    placeholder={`Expected: ${lotDetails?.expected_qty || '-'}`}
                    style={{ width: "100%", padding: "14px", borderRadius: "12px", border: "1px solid #cbd5e1", outline: "none", fontWeight: 600, color: "#1e293b", backgroundColor: "#f8fafc" }}
                  />
                </div>

                <div>
                  <label style={{ fontWeight: 700, color: "#334155", marginBottom: "10px", display: "block", fontSize: "15px" }}>Photo Evidence <span className="text-danger">*</span></label>
                  
                  <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", marginBottom: "12px" }}>
                    {photos.map((file, idx) => (
                      <div key={idx} style={{ position: "relative", width: "80px", height: "80px" }}>
                        <img src={URL.createObjectURL(file)} alt="preview" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "10px", border: "2px solid #e2e8f0" }} />
                        <button 
                          onClick={() => setPhotos(photos.filter((_, i) => i !== idx))}
                          style={{ position: "absolute", top: -8, right: -8, background: "#ef4444", color: "white", border: "none", borderRadius: "50%", width: 24, height: 24, fontSize: 12, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", boxShadow: "0 2px 4px rgba(0,0,0,0.2)" }}
                        >✕</button>
                      </div>
                    ))}
                    
                    <button 
                      type="button"
                      onClick={() => fileInputRef.current.click()}
                      style={{ width: "80px", height: "80px", background: "#f1f5f9", border: "2px dashed #94a3b8", borderRadius: "10px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "#64748b", cursor: "pointer", transition: "background 0.2s" }}
                      onMouseOver={e => e.currentTarget.style.background = "#e2e8f0"}
                      onMouseOut={e => e.currentTarget.style.background = "#f1f5f9"}
                    >
                      <i className="bi bi-camera" style={{ fontSize: "24px" }}></i>
                    </button>
                  </div>
                  
                  <input 
                    type="file" 
                    ref={fileInputRef} 
                    onChange={e => {
                      if (e.target.files?.length) {
                        setPhotos([...photos, ...Array.from(e.target.files)]);
                      }
                      e.target.value = null;
                    }}
                    accept="image/*" 
                    capture="environment"
                    multiple
                    style={{ display: "none" }} 
                  />
                  <p style={{ fontSize: "13px", color: "#64748b", margin: 0, fontWeight: 500 }}>Upload clear photos of the defect.</p>
                </div>

                <div>
                  <label style={{ fontWeight: 700, color: "#334155", marginBottom: "10px", display: "block", fontSize: "15px" }}>Comments</label>
                  <textarea 
                    value={comments}
                    onChange={e => setComments(e.target.value)}
                    placeholder="Describe the issue..."
                    rows={3}
                    style={{ width: "100%", padding: "14px", borderRadius: "12px", border: "1px solid #cbd5e1", outline: "none", resize: "none", fontWeight: 500, color: "#1e293b", backgroundColor: "#f8fafc" }}
                  />
                </div>

                <div style={{ display: "flex", gap: "16px", marginTop: "12px" }}>
                  <button 
                    onClick={() => setShowFailForm(false)}
                    style={{ flex: 1, padding: "16px", background: "#f1f5f9", border: "none", color: "#475569", borderRadius: "12px", fontWeight: 700, fontSize: "16px" }}
                  >
                    Cancel
                  </button>
                  <button 
                    onClick={() => submitQC('FAIL')}
                    disabled={submitting}
                    style={{ flex: 2, padding: "16px", background: "#ef4444", border: "none", color: "white", borderRadius: "12px", fontWeight: 800, fontSize: "16px", boxShadow: "0 10px 15px -3px rgba(239, 68, 68, 0.4)" }}
                  >
                    {submitting ? 'Raising Ticket...' : 'Raise Ticket'}
                  </button>
                </div>

              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
